import type { PrepDashboardSnapshotResponse } from '../api/prep'

export const PREP_DASHBOARD_CACHE_SCHEMA = 1
export const PREP_DASHBOARD_CACHE_TTL_MS = 15 * 60 * 1000
export const PREP_DASHBOARD_CACHE_MAX_CHARS = 3_500_000

interface PrepDashboardCacheRecord {
  schema: number
  savedAt: number
  userId: number
  stationId: number
  fingerprint: string
  response: PrepDashboardSnapshotResponse
}

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export const buildPrepDashboardCacheKey = (userId: number, stationId: number): string =>
  `rpa:prep-dashboard:v${PREP_DASHBOARD_CACHE_SCHEMA}:user:${Math.max(0, userId)}:station:${Math.max(0, stationId)}`

export const readPrepDashboardCache = (
  userId: number,
  stationId: number,
  fingerprint: string,
  now = Date.now(),
): PrepDashboardCacheRecord | null => {
  const storage = getStorage()
  if (!storage || userId <= 0 || stationId <= 0) {
    return null
  }

  const key = buildPrepDashboardCacheKey(userId, stationId)
  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return null
    }
    if (raw.length > PREP_DASHBOARD_CACHE_MAX_CHARS) {
      storage.removeItem(key)
      return null
    }

    const record = JSON.parse(raw) as Partial<PrepDashboardCacheRecord>
    const valid =
      record.schema === PREP_DASHBOARD_CACHE_SCHEMA &&
      record.userId === userId &&
      record.stationId === stationId &&
      record.fingerprint === fingerprint &&
      typeof record.savedAt === 'number' &&
      now >= record.savedAt &&
      now - record.savedAt <= PREP_DASHBOARD_CACHE_TTL_MS &&
      record.response !== null &&
      typeof record.response === 'object' &&
      Array.isArray(record.response.cards) &&
      Array.isArray(record.response.items)

    if (!valid) {
      storage.removeItem(key)
      return null
    }

    return record as PrepDashboardCacheRecord
  } catch {
    try {
      storage.removeItem(key)
    } catch {
      // Ignore unavailable/quota-limited storage.
    }
    return null
  }
}

export const writePrepDashboardCache = (
  userId: number,
  stationId: number,
  fingerprint: string,
  response: PrepDashboardSnapshotResponse,
  savedAt = Date.now(),
): boolean => {
  const storage = getStorage()
  if (!storage || userId <= 0 || stationId <= 0) {
    return false
  }

  const key = buildPrepDashboardCacheKey(userId, stationId)
  try {
    const serialized = JSON.stringify({
      schema: PREP_DASHBOARD_CACHE_SCHEMA,
      savedAt,
      userId,
      stationId,
      fingerprint,
      response,
    } satisfies PrepDashboardCacheRecord)

    if (serialized.length > PREP_DASHBOARD_CACHE_MAX_CHARS) {
      storage.removeItem(key)
      return false
    }

    storage.setItem(key, serialized)
    return true
  } catch {
    return false
  }
}
